import { Component, EventEmitter, Input, OnChanges, Output } from '@angular/core'
import { ProjectWithStatus } from '@modules/private/worklog/data/interfaces/project.interface'
import {
  COMMERCIAL_STATUS_META,
  EpicCategory,
  EPIC_CATEGORIES,
  PROJECT_STATUS_META,
  PROJECT_STATUS_ORDER,
} from '../../data/constants/roadmap.constants'

interface CategoryGroup {
  category: EpicCategory
  projects: ProjectWithStatus[]
  arrancados: number
  total: number
  progress: number
}

/**
 * Rejilla de PROYECTOS: las iniciativas grandes, la mayoría todavía sin arrancar (ADR-0048 · D1).
 *
 * Sustituye al vision board de épicas, que mezclaba dos cosas distintas: frentes de trabajo que ya
 * ocurrieron y proyectos que aún no empiezan. Los primeros se leen en Tiempos, donde su duración
 * significa algo; aquí quedan los segundos, que es de lo que trata la pantalla.
 *
 * El estado lo deriva el backend, así que esta vista no calcula ninguno: solo lo pinta. La
 * distinción que justifica la pantalla es `idea` vs `scoped` — registrado y nada más, contra ya
 * con entregables o precio. Antes los dos se veían igual, como «Propuesta».
 */
@Component({
  selector: 'app-roadmap-projects',
  templateUrl: './roadmap-projects.component.html',
  styleUrls: ['./roadmap-projects.component.scss'],
})
export class RoadmapProjectsComponent implements OnChanges {
  @Input() projects: ProjectWithStatus[] | null = null
  /** Mientras carga no se puede afirmar que no hay proyectos: son dos estados, no uno. */
  @Input() loading = false
  /** Lo comercial solo viaja para Admin; sin esto la tarjeta reservaría hueco a un dato ausente. */
  @Input() canSeePrice = false
  @Output() select = new EventEmitter<ProjectWithStatus>()
  @Output() create = new EventEmitter<void>()

  groups: CategoryGroup[] = []

  readonly PROJECT_STATUS_META = PROJECT_STATUS_META
  readonly COMMERCIAL_STATUS_META = COMMERCIAL_STATUS_META

  ngOnChanges(): void {
    const all = this.projects ?? []
    this.groups = EPIC_CATEGORIES.map((category) => {
      const ps = all
        .filter((p) => p.project.category === category.id)
        .sort(
          (a, b) =>
            PROJECT_STATUS_ORDER.indexOf(a.status) - PROJECT_STATUS_ORDER.indexOf(b.status) ||
            a.project.order - b.project.order
        )
      //* "Arrancados" y no "conseguidos": en una pantalla de cosas por hacer, lo que importa es
      //* cuántas dejaron de ser una idea, no cuántas terminaron.
      const arrancados = ps.filter((p) => p.status === 'in_progress' || p.status === 'delivered').length
      return {
        category,
        projects: ps,
        arrancados,
        total: ps.length,
        progress: ps.length ? Math.round((arrancados / ps.length) * 100) : 0,
      }
    }).filter((g) => g.total > 0)
  }

  onPick(p: ProjectWithStatus): void {
    this.select.emit(p)
  }

  /**
   * Qué barra enseña la tarjeta. Con épicas, el avance del trabajo; sin ellas, cuántos entregables
   * están cerrados. Un proyecto sin ninguna de las dos cosas no enseña barra: no hay nada medido y
   * una barra en cero se lee como "no avanzamos" en vez de "no ha empezado".
   */
  progressOf(p: ProjectWithStatus): { pct: number; label: string } | null {
    if (p.taskCount) {
      return {
        pct: Math.round((p.doneCount / p.taskCount) * 100),
        label: `${p.doneCount}/${p.taskCount} tareas${p.points ? ` · ${p.points} pts` : ''}`,
      }
    }
    if (p.deliverablesTotal) {
      return {
        pct: Math.round((p.deliverablesDone / p.deliverablesTotal) * 100),
        label: `${p.deliverablesDone}/${p.deliverablesTotal} entregables`,
      }
    }
    return null
  }

  /** Precio legible. Solo se llama cuando el bloque comercial llegó, o sea solo para Admin. */
  priceLabel(p: ProjectWithStatus): string | null {
    const c = p.project.commercial
    if (!c || c.price == null) return null
    const monto = c.price.toLocaleString('es-MX', { style: 'currency', currency: c.currency || 'MXN', maximumFractionDigits: 0 })
    return `${monto}${c.withVat ? ' c/IVA' : ' + IVA'}`
  }

  dateLabel(p: ProjectWithStatus): string {
    return p.project.targetDate ?? 'Sin fecha'
  }

  trackProject(_: number, p: ProjectWithStatus): string {
    return p.project._id
  }
}
